import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, defer, map, repeat, retry, takeWhile, tap, throwError, timer } from 'rxjs';
import { environment } from '../../environments/environment';

export type ExecutionJobStatus = 'QUEUED' | 'RUNNING' | 'PASSED' | 'FAILED' | 'ERROR' | 'CANCELLED';

export interface ExecutionJob {
  id: string;
  projectId: string;
  testCaseId: string;
  status: ExecutionJobStatus;
  runnerId: string | null;
  testRunId: string | null;
  protocol: string[];
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** Anforderung des Auftrags; leer bei einem Auftrag an einen bestimmten Rechner. */
  requiredLabels: string[];
  /** null, solange dem Auftrag kein Rechner zugeordnet ist. */
  runnerName: string | null;
  /** Schluessel des Testlaufs, etwa TR-552; null, solange kein Testlauf zugeordnet ist. */
  testRunKey: string | null;
  /** null, wenn die Person, die den Auftrag gestartet hat, nicht mehr aufloesbar ist. */
  requestedByName: string | null;
  /** true, wenn der Auftrag vom angemeldeten Nutzer stammt. */
  requestedByCaller: boolean;
  /** true, wenn ein Abbruch angefordert ist und der Rechner ihn noch nicht bestaetigt hat (NEX-1661). */
  cancelRequested: boolean;
  /** Art des Auftrags (NEX-1710); fehlt bei aelteren Antworten. */
  kind?: 'TEST_RUN' | 'RECORDING';
  /** Bibliothek einer Aufnahme; null bei einem Testlauf. */
  objectMappingId?: string | null;
}

/**
 * Wohin ein automatisierter Lauf geht: an genau einen Rechner oder an jeden verbundenen Rechner,
 * der alle Etiketten traegt. Nie beides — der Server lehnt das mit 400 ab.
 */
export type RunTarget = { kind: 'runner'; runnerId: string; runnerName: string } | { kind: 'labels'; labels: string[] };

/** Ausschnitt eines verbundenen Rechners aus `GET …/execution-jobs/runners`. */
export interface RecordingRunner {
  id: string;
  canRecord: boolean;
}

const FINAL: ReadonlySet<ExecutionJobStatus> = new Set<ExecutionJobStatus>(['PASSED', 'FAILED', 'ERROR', 'CANCELLED']);

export function isFinalStatus(status: ExecutionJobStatus): boolean {
  return FINAL.has(status);
}

/** Textschluessel unter testCases.run.cancel fuer einen gescheiterten Abbruch. */
export type CancelFailureKey = 'alreadyFinished' | 'failed';

/** 409 heisst: Der Auftrag war schon im Endstand. Alles andere ist ein Fehlschlag. */
export function cancelFailureKey(error: unknown): CancelFailureKey {
  return error instanceof HttpErrorResponse && error.status === 409 ? 'alreadyFinished' : 'failed';
}

const POLL_RETRY_BASE_DELAY_MS = 2000;
const POLL_RETRY_MAX_DELAY_MS = 10_000;
/** Ein Neustart des Backends mit Flyway dauert deutlich länger als ein paar Sekunden. */
const POLL_OUTAGE_LIMIT_MS = 60_000;
/** Kein Zugriff oder Auftrag weg: eine Wiederholung bringt nichts. */
const POLL_PERMANENT_STATUSES: ReadonlySet<number> = new Set([403, 404]);
const POLL_SLOW_AFTER_MS = 60_000;
const POLL_SLOW_INTERVAL_MS = 5000;
const POLL_GIVE_UP_AFTER_MS = 15 * 60_000;

/** Die Verfolgung hat aufgegeben; der Auftrag selbst kann trotzdem noch laufen. */
export class PollTimeoutError extends Error {
  constructor() {
    super('Execution job is no longer being tracked.');
    this.name = 'PollTimeoutError';
  }
}

@Injectable({ providedIn: 'root' })
export class ExecutionJobService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  /**
   * Die gerade verbundenen Rechner mit ihrer Faehigkeit, aufzunehmen (NEX-1719). Ausgeschaltete
   * fehlen hier; die Auswahl laedt sie weiter ueber `RunnerPairingService.listRunners()`.
   */
  listRecordingRunners(projectId: string): Observable<RecordingRunner[]> {
    return this.http.get<RecordingRunner[]>(`${this.base}/${projectId}/execution-jobs/runners`);
  }

  /**
   * Reiht einen Testfall zur automatisierten Ausführung ein.
   *
   * Der Auftrag geht an genau einen Rechner oder an jeden verbundenen, der alle Etiketten traegt
   * (NEX-1257). Ist kein passender Rechner verbunden, lehnt der Server mit 409 ab, statt den
   * Auftrag stumm in der Warteschlange liegen zu lassen. Ohne Ziel lehnt er mit 400 ab (NEX-1509).
   */
  enqueue(projectId: string, testCaseId: string, target: RunTarget): Observable<ExecutionJob> {
    const params =
      target.kind === 'runner'
        ? new HttpParams().set('runnerId', target.runnerId)
        : target.labels.reduce((acc, label) => acc.append('requiredLabels', label), new HttpParams());
    return this.http.post<ExecutionJob>(
      `${this.base}/${projectId}/test-cases/${testCaseId}/execution-jobs`,
      {},
      { params },
    );
  }

  /**
   * Wie viele Schritte des gespeicherten Testfalls automatisiert laufen koennen, Module aufgeloest
   * (NEX-1505). Bei 0 lehnt der Server das Einreihen ab.
   */
  executableStepCount(projectId: string, testCaseId: string): Observable<number> {
    return this.http
      .get<{ executableStepCount: number }>(`${this.base}/${projectId}/test-cases/${testCaseId}/executable-steps`)
      .pipe(map((response) => response.executableStepCount));
  }

  /** Der juengste Auftrag eines Testfalls, egal wer ihn gestartet hat; null, wenn es keinen gibt. */
  latestForTestCase(projectId: string, testCaseId: string): Observable<ExecutionJob | null> {
    const params = new HttpParams().set('testCaseId', testCaseId).set('limit', 1);
    return this.http
      .get<ExecutionJob[]>(`${this.base}/${projectId}/execution-jobs`, { params })
      .pipe(map((jobs) => jobs[0] ?? null));
  }

  get(projectId: string, jobId: string): Observable<ExecutionJob> {
    return this.http.get<ExecutionJob>(`${this.base}/${projectId}/execution-jobs/${jobId}`);
  }

  /**
   * Bricht einen Auftrag ab. Ein wartender endet sofort; bei einem laufenden kommt er mit
   * cancelRequested zurueck und endet erst, wenn der Rechner aufhoert.
   */
  cancel(projectId: string, jobId: string): Observable<ExecutionJob> {
    return this.http.post<ExecutionJob>(`${this.base}/${projectId}/execution-jobs/${jobId}/cancel`, {});
  }

  /**
   * Verfolgt einen Auftrag bis zum Endstand. Bewusst Abfrage statt Push: der Auftrag läuft
   * Sekunden bis Minuten, und eine offene Verbindung je Zuschauer wäre für diesen Zweck
   * unverhältnismäßig.
   *
   * Die nächste Abfrage startet erst, wenn die vorige beantwortet ist; eine langsame Antwort
   * wird also nie abgebrochen. Ein Ausfall (Neustart, 401 nach erneuerter Anmeldung) wird mit
   * wachsender Pause wiederholt, solange seit der letzten Antwort weniger als eine Minute
   * vergangen ist; 403 und 404 gehen sofort als Fehler nach oben. Bleibt der Auftrag liegen,
   * wird der Takt nach einer Minute langsamer, nach 15 Minuten endet die Verfolgung mit
   * {@link PollTimeoutError}.
   */
  poll(projectId: string, jobId: string, intervalMs = 1500): Observable<ExecutionJob> {
    return defer(() => {
      const startedAt = Date.now();
      let lastSuccessAt = startedAt;
      return this.get(projectId, jobId).pipe(
        tap(() => (lastSuccessAt = Date.now())),
        retry({
          delay: (error: unknown, attempt: number) => {
            const permanent = error instanceof HttpErrorResponse && POLL_PERMANENT_STATUSES.has(error.status);
            if (permanent || Date.now() - lastSuccessAt >= POLL_OUTAGE_LIMIT_MS) {
              return throwError(() => error);
            }
            return timer(Math.min(POLL_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), POLL_RETRY_MAX_DELAY_MS));
          },
        }),
        repeat({
          delay: () => {
            const elapsed = Date.now() - startedAt;
            if (elapsed >= POLL_GIVE_UP_AFTER_MS) {
              return throwError(() => new PollTimeoutError());
            }
            return timer(elapsed >= POLL_SLOW_AFTER_MS ? POLL_SLOW_INTERVAL_MS : intervalMs);
          },
        }),
        takeWhile((job) => !isFinalStatus(job.status), true),
      );
    });
  }
}
